// Components
import { useEffect, useRef } from 'react';

import Alert from '@amzn/awsui-components-react/polaris/alert';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import ButtonDropdown from '@amzn/awsui-components-react/polaris/button-dropdown';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { ComplexSignupDialog } from '../complexSignup/signupDialog';
import { publishKatalMetric } from '../katalAnalytics';
import { TimeModal } from './timeModal';
// Hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import {
  useCreateSignup,
  useDeleteSignup,
  useLatestSignupMutationState,
} from '../../hooks/shifts/shiftSignup';
import { useBulkLeaveWaitlist, useJoinWaitlist } from '../../hooks/shifts/shiftWaitlist';
import { useUser } from '../../hooks/user';
// Types
import {
  Attendance,
  AttendanceStatus,
  Event,
  EventStatus,
  Shift,
  Signup,
  Source,
  WaitlistEntry,
} from '@amzn/red-velvet-api';
// Utils
import { useAttendance, useTrackTime } from '../../hooks/attendance/attendanceMutation';
import {
  EVENT_CREATION,
  SHIFT_WAITLIST,
  VOLUNTEER_WAITLIST_JOIN_CLICK_COMPLEX_SIGNUP_MODAL,
  VOLUNTEER_WAITLIST_JOIN_CLICK_SIMPLE_SIGNUP,
  VOLUNTEER_WAITLIST_LEAVE_CONFIRM,
  VOLUNTEER_WAITLIST_LEAVE_MODAL,
} from '../../utils/constants';
import { isUnlistedEvent } from '../../utils/eventUtils';
import { getSignupVisibleQuestions } from '../../utils/signupUtils';
import { isShiftFull, isWaitlistEnabled } from '../../utils/volunteerEventShiftsTableUtils';

interface ActionLinkProps {
  event: Event;
  shift: Shift;
  signup?: Signup;
  attendance?: Attendance;
  forceCancelSignup?: boolean;
  /**
   * One-click join deep link matched this row while it offers Sign Up (spots opened since the email).
   * ToggleSignup auto-fires the signup (simple event) or opens the complex-signup dialog.
   */
  forceSignup?: boolean;
  /**
   * Render the action as inline-link text instead of a full-width button. Set only by the new
   * VolunteerEventShiftsTable (gated on the ShiftWaitlist flag) so its sticky Action column matches
   * CoordinatorEventShiftsTable's link actions; the carousel and signup cards keep their buttons.
   */
  linkStyle?: boolean;
}

export function ShiftActions(props: ActionLinkProps) {
  const { event, shift, forceCancelSignup, forceSignup, signup, attendance, linkStyle } = props;

  const [modalVisible, setModalVisible] = useState(false);
  const shiftStatus = useLatestSignupMutationState(event.eventId, shift.shiftId, signup);

  const hasUserParticipated = attendance !== undefined || shiftStatus.signup !== undefined;

  if (hasUserParticipated && shift.complete) {
    return (
      <TrackPastData
        event={event}
        shift={shift}
        signup={shiftStatus.signup}
        attendance={attendance}
        startComplexSignup={() => setModalVisible(true)}
        linkStyle={linkStyle}
      />
    );
  }

  return (
    <>
      <ToggleSignup
        event={event}
        shift={shift}
        signup={shiftStatus.signup}
        startComplexSignup={() => setModalVisible(true)}
        forceCancelSignup={forceCancelSignup ?? false}
        forceSignup={forceSignup ?? false}
        linkStyle={linkStyle}
      />
      <ComplexSignupDialog
        event={event}
        shift={shift}
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
      />
    </>
  );
}

export function requiresComplexSignup(event: Event, includeGuestsCheck = true) {
  return (
    (includeGuestsCheck && event.allowGuests) ||
    (event.questions && event.questions.length > 0) ||
    (event.waiver && event.waiver.acceptance !== 'none' && event.waiver.url) ||
    event.hasTShirt
  );
}

export function getEditEventUrl(event: Event, isEventCreationEnabled: boolean): string {
  if (!isEventCreationEnabled) return `https://amazon.benevity.org/volunteer/${event.eventId}/edit`;
  return event.status === EventStatus.DRAFT
    ? `/create-event?draftId=${event.eventId}`
    : `/edit-event?id=${event.eventId}`;
}

export function EditEventLink({ event, source }: { event: Event; source?: string }) {
  const { t } = useTranslation();
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);

  const linkText = isEventCreationEnabled
    ? 'eventDetails.shiftDisplay.edit'
    : 'eventDetails.shiftDisplay.editEventBenevity';

  const linkUrl = getEditEventUrl(event, !!isEventCreationEnabled);

  return (
    <Button
      href={linkUrl}
      target="_blank"
      external={!isEventCreationEnabled}
      onFollow={() => {
        if (source) publishKatalMetric('editEvent_entryPoint', source);
      }}
    >
      {t(linkText)}
    </Button>
  );
}

export interface ManageEventButtonProps {
  event: Event;
  shifts: Shift[];
  hasCompletedShifts?: boolean;
  hasSignupsOrAttendances?: boolean;
  onDelete?: () => void;
}

export function ManageEventButton({
  event,
  shifts,
  hasCompletedShifts = false,
  hasSignupsOrAttendances = false,
  onDelete,
}: ManageEventButtonProps) {
  const namespaces = ['activity', 'translation'];
  const { t } = useTranslation(namespaces);
  const navigate = useNavigate();
  const features = useFeatures();
  const [searchParams] = useSearchParams();

  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  // If the event's required fields: Primary Activity, Focus Area, and ACI Grant have not been completed,
  // we need to open the Step 1: Event Detail  (0-indexed) first instead of Step 2: Track Attendance
  const wizardPageToNavigate =
    event.primaryActivity && event.focusArea && event.usedAciGrant != null ? 1 : 0;

  const editEventUrl =
    isUnlistedEvent(event) && shifts.length === 1
      ? `/track-attendance?eventId=${event.eventId}&shiftId=${shifts[0].shiftId}`
      : isEventCreationEnabled && !isUnlistedEvent(event)
        ? event.status === EventStatus.DRAFT
          ? `/create-event?draftId=${event.eventId}`
          : `/edit-event?id=${event.eventId}`
        : undefined;

  const givehubEditUrl = `https://amazon.benevity.org/volunteer/${event.eventId}/edit`;

  // Unlisted events always carry attendance records but are still deletable: the backend
  // bypasses the signups/attendances gate for them and cascade-deletes their attendance.
  // Non-unlisted events stay blocked when signups or attendances exist.
  const blockDelete = !isUnlistedEvent(event) && hasSignupsOrAttendances;

  const items = [
    ...(editEventUrl
      ? [
          {
            id: 'editEvent',
            text: t('coordinatorEventShiftsTable.actions.editEvent'),
            href: editEventUrl,
          },
        ]
      : []),
    ...(event.source === Source.GIVEHUB && !event.vcCreated
      ? [
          {
            id: 'editEventInGivehub',
            text: t('coordinatorEventShiftsTable.actions.editEventInGivehub'),
            href: givehubEditUrl,
            external: true,
          },
        ]
      : []),
    ...(hasCompletedShifts
      ? [
          {
            id: 'trackAttendanceEventLevel',
            text: t('coordinatorEventShiftsTable.actions.bulkUpload.trackAttendanceEventLevel'),
          },
        ]
      : []),
    ...(onDelete && isEventCreationEnabled
      ? [
          {
            id: 'deleteEvent',
            text: t('coordinatorEventShiftsTable.actions.deleteEvent'),
            disabled: blockDelete,
            disabledReason: t('coordinatorEventShiftsTable.actions.deleteEventDisabledReason'),
          },
        ]
      : []),
    ...(isShiftWaitlistEnabled
      ? [
          {
            id: 'manageWaitlist',
            text: t('coordinatorEventShiftsTable.actions.manageWaitlist'),
            disabled: isUnlistedEvent(event),
            disabledReason: t('coordinatorEventShiftsTable.actions.manageWaitlistDisabledReason'),
          },
        ]
      : []),
  ];

  if (items.length === 0) return null;

  return (
    <ButtonDropdown
      items={items}
      onItemClick={({ detail }) => {
        if (detail.id === 'trackAttendanceEventLevel') {
          publishKatalMetric('attendance_bulkUploadEventLevelTrackingWizardOpened', '');
          const urlFeatures = searchParams.get('features');
          navigate(
            `/track-attendance?eventId=${event.eventId}&page=${wizardPageToNavigate}${urlFeatures ? '&features=' + urlFeatures : ''}`
          );
        } else if (detail.id === 'deleteEvent') {
          onDelete?.();
        } else if (detail.id === 'manageWaitlist') {
          publishKatalMetric('manageWaitlist_entryPoint', 'coordinatorActivity');
          const urlFeatures = searchParams.get('features');
          navigate(
            `/manage-waitlist?eventId=${event.eventId}${urlFeatures ? '&features=' + urlFeatures : ''}`
          );
        }
      }}
      onItemFollow={({ detail }) => {
        if (detail.id === 'editEvent') {
          publishKatalMetric('editEvent_entryPoint', 'coordinatorActivity');
        } else if (detail.id === 'editEventInGivehub') {
          publishKatalMetric('editEventInGivehub_entryPoint', 'coordinatorActivity');
        }
      }}
    >
      {t('coordinatorEventShiftsTable.actions.eventActionsButton')}
    </ButtonDropdown>
  );
}

type ActionInput = {
  event: Event;
  shift: Shift;
  signup: Signup | undefined;
  attendance?: Attendance;
  startComplexSignup: () => void;
  myActivityEnabled?: boolean;
  forceCancelSignup?: boolean;
  /** One-click join deep link: auto-fire the signup (or open the complex dialog) once. */
  forceSignup?: boolean;
  /** See ActionLinkProps.linkStyle — inline-link rendering for the new VolunteerEventShiftsTable. */
  linkStyle?: boolean;
};

export function TrackPastData({
  event,
  shift,
  signup,
  attendance,
  startComplexSignup,
  linkStyle,
}: ActionInput) {
  return (
    <div>
      <SpaceBetween direction="horizontal" size="s">
        <TrackAttendance
          event={event}
          shift={shift}
          signup={signup}
          attendance={attendance}
          startComplexSignup={startComplexSignup}
          linkStyle={linkStyle}
        />
      </SpaceBetween>
    </div>
  );
}

export function TrackAttendance({ event, shift, linkStyle }: ActionInput) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const timeMutation = useTrackTime(shift.eventId, shift.shiftId);
  const user = useUser();
  const attendance = useAttendance(event.eventId, shift.shiftId, user.userId);

  return (
    <>
      <SpaceBetween direction="horizontal" size="s">
        {attendance.data?.attendanceStatus !== 'DID_NOT_ATTEND' ? (
          <Button
            fullWidth={!linkStyle}
            wrapText={linkStyle ? false : undefined}
            variant={linkStyle ? 'inline-link' : undefined}
            loading={timeMutation.isPending}
            data-testid="attendace.didNotAttendButton"
            data-aci-analytics-name="shifts_didNotAttend"
            onClick={() =>
              timeMutation.mutate({
                eventId: shift.eventId,
                shiftId: shift.shiftId,
                alias: user.userId,
                minutes: 0,
                attendanceStatus: AttendanceStatus.DID_NOT_ATTEND,
              })
            }
          >
            {t('eventDetails.shiftDisplay.didNotAttend')}
          </Button>
        ) : null}
        <Button
          fullWidth={!linkStyle}
          wrapText={linkStyle ? false : undefined}
          variant={linkStyle ? 'inline-link' : attendance.data ? undefined : 'primary'}
          data-aci-analytics-name="shifts_attendanceDialog"
          onClick={() => setModalVisible(true)}
        >
          {attendance.data && attendance.data.attendanceStatus === 'ATTENDED'
            ? t('eventDetails.shiftDisplay.editTime')
            : t('eventDetails.shiftDisplay.trackTime')}
        </Button>
      </SpaceBetween>
      <TimeModal shifts={[shift]} visible={modalVisible} onDismiss={() => setModalVisible(false)} />
    </>
  );
}

/**
 * Single-link attendance action for the VolunteerEventShiftsTable Action column (ShiftWaitlist-gated).
 * Replaces the two-button TrackAttendance in that table per the Figma: one "Track attendance" /
 * "Edit attendance" link that opens TimeModal in the right mode:
 *  - signed up OR has an attendance record -> 'attendance'    (Attended + time / Did not attend -> "Save attendance")
 *  - otherwise (first-time walk-in)        -> 'volunteerTime' (time only -> "Save volunteer time")
 * Gating 'attendance' on an existing record (not just signup) lets a walk-in who mistakenly tracked
 * time correct it to "Did not attend" — volunteerTime mode is time-only and rejects 0 minutes.
 * The old carousel/table keep the two-button TrackAttendance; only this ShiftWaitlist-gated table
 * uses this single-link action (independent of page — including My Activity > Volunteer).
 */
export function TrackAttendanceLink({
  shift,
  signup,
  attendance,
  linkStyle,
}: {
  shift: Shift;
  signup?: Signup;
  attendance?: Attendance;
  linkStyle?: boolean;
}) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  // Any existing attendance record (ATTENDED or DID_NOT_ATTEND) means this is an edit, not a
  // first-time track — so label it "Edit attendance". Previously only ATTENDED did, leaving a
  // DID_NOT_ATTEND record mislabelled "Track attendance".
  const hasAttendanceRecord = attendance !== undefined;

  return (
    <>
      <Button
        fullWidth={!linkStyle}
        wrapText={linkStyle ? false : undefined}
        variant={linkStyle ? 'inline-link' : undefined}
        data-testid="attendance.trackAttendanceLink"
        data-aci-analytics-name="shifts_trackAttendance"
        onClick={() => setModalVisible(true)}
      >
        {hasAttendanceRecord
          ? t('eventDetails.shiftDisplay.editAttendance')
          : t('eventDetails.shiftDisplay.trackAttendance')}
      </Button>
      <TimeModal
        shifts={[shift]}
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        attendanceMode={
          signup !== undefined || attendance !== undefined ? 'attendance' : 'volunteerTime'
        }
      />
    </>
  );
}

/**
 * In-progress FLEXIBLE shift the volunteer is already signed up to (no time tracked yet): a kebab
 * offering "Cancel Sign Up" or "Track attendance" (cce-7837). Reuses the delete-signup mutation and
 * the TimeModal so it stays consistent with ToggleSignup / TrackAttendanceLink. Cancel is a plain
 * cancel (no waitlist promote-confirm): an in-progress shift no longer auto-promotes from the waitlist.
 */
export function CancelOrTrackActions({
  event,
  shift,
  signup,
  attendance,
}: {
  event: Event;
  shift: Shift;
  signup?: Signup;
  attendance?: Attendance;
}) {
  const { t } = useTranslation();
  const deleteMutation = useDeleteSignup(event.eventId, shift.shiftId);
  const [timeModalVisible, setTimeModalVisible] = useState(false);

  const cancelSignup = () => {
    if (signup) {
      deleteMutation.mutate({
        eventId: shift.eventId,
        shift,
        request: { signupId: signup.signupId },
      });
    }
  };

  return (
    <>
      <ButtonDropdown
        variant="inline-icon"
        expandToViewport
        loading={deleteMutation.isPending}
        ariaLabel={t('volunteerEventShiftsTable.moreActions')}
        data-testid="shifts.cancelOrTrack"
        items={[
          { id: 'cancelSignup', text: t('eventDetails.shiftDisplay.cancel') },
          { id: 'trackAttendance', text: t('eventDetails.shiftDisplay.trackAttendance') },
        ]}
        onItemClick={({ detail }) => {
          if (detail.id === 'cancelSignup') {
            cancelSignup();
          } else if (detail.id === 'trackAttendance') {
            setTimeModalVisible(true);
          }
        }}
      />
      <TimeModal
        shifts={[shift]}
        visible={timeModalVisible}
        onDismiss={() => setTimeModalVisible(false)}
        attendanceMode={
          signup !== undefined || attendance !== undefined ? 'attendance' : 'volunteerTime'
        }
      />
    </>
  );
}

/**
 * Confirm dialog for cancelling a signup on a FULL, waitlist-enabled shift (Req 13): auto-signup
 * promotes the next waitlisted person immediately (no grace period), so this confirmation replaces
 * the grace period for accidental cancellations. Presentational — ToggleSignup owns the mutation.
 */
function WaitlistCancelConfirmModal({
  visible,
  pending,
  onDismiss,
  onConfirm,
}: {
  visible: boolean;
  pending: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const I18N = 'eventDetails.shiftDisplay.cancelSignupConfirm';
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={t(`${I18N}.title`)}
      closeAriaLabel={t(`${I18N}.cancel`)}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={onDismiss}
              data-testid="cancelSignupConfirm.cancelButton"
            >
              {t(`${I18N}.cancel`)}
            </Button>
            <Button
              variant="primary"
              loading={pending}
              data-testid="cancelSignupConfirm.confirmButton"
              data-aci-analytics-name="shifts_cancel_signup_confirm"
              onClick={onConfirm}
            >
              {t(`${I18N}.confirm`)}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Alert type="warning">{t(`${I18N}.message`)}</Alert>
    </Modal>
  );
}

// Although this would be more readable as two separate components (one for delete and one for signup)
// this causes problems with the accessibility for Complex signups.  When they are one component, the
// Button that was focused when a complex signup dialog is opened is still there when the dialog is closed
// so the keyboard focus automatically returns to it.  If they are separate components, the focus is lost
// when the dialog is closed, which means keyboard users have to re-navigate through the whole page
export function ToggleSignup({
  event,
  shift,
  signup,
  startComplexSignup,
  forceCancelSignup,
  forceSignup,
  linkStyle,
}: ActionInput) {
  const { t } = useTranslation();
  const signupMutation = useCreateSignup(event.eventId, shift.shiftId);
  const deleteMutation = useDeleteSignup(event.eventId, shift.shiftId);
  const user = useUser();
  const hasCancelled = useRef(false);
  const hasForceSignedUp = useRef(false);
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const isShiftWaitlistFeatureOn = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  const isDelete = signup !== undefined;
  const signupEvent = {
    ...event,
    questions: getSignupVisibleQuestions(event.questions, isEventCreationEnabled),
  };
  const isComplexShiftSignup = requiresComplexSignup(signupEvent);
  // TODO: Enforce metrics in integ test: https://issues.amazon.com/issues/cce-3970
  const signup_metric = isComplexShiftSignup ? 'complex_signup_dialog' : 'shifts_signup';

  // Req 13: cancelling on a full, waitlist-enabled shift hands the spot to the next waitlisted
  // person immediately (auto-signup has no grace period), so those cancels confirm first. Feature
  // flag-gated so pre-launch surfaces never show waitlist language. The one-click email cancel
  // (forceCancelSignup below) stays immediate — the email click is the explicit intent.
  const needsCancelConfirm =
    isShiftWaitlistFeatureOn && isShiftFull(shift) && isWaitlistEnabled(shift);

  // Auto-trigger delete mutation if one-click cancel signup is found.
  useEffect(() => {
    if (forceCancelSignup && isDelete && !shift.complete && signup && !hasCancelled.current) {
      hasCancelled.current = true;

      // GOLDEN PATH NOTE
      // Defer the mutation call until after the current render cycle completes to allow
      // React Query to properly attach to the component context
      queueMicrotask(() => {
        deleteMutation.mutate({
          eventId: shift.eventId,
          shift,
          request: { signupId: signup.signupId },
        });
      });
    }
  }, [forceCancelSignup, isDelete, signup, shift, deleteMutation]);

  // One-click join deep link, spots-open case: this row offers Sign Up (ActionCell only sets
  // forceSignup on SIGN_UP/CANCEL rows and isDelete rules out the latter), so fire the signup —
  // or open the complex dialog when the event needs input the deep link cannot supply.
  useEffect(() => {
    if (forceSignup && !isDelete && !shift.complete && !hasForceSignedUp.current) {
      hasForceSignedUp.current = true;

      // GOLDEN PATH NOTE — defer past the current render cycle (see forceCancelSignup above).
      queueMicrotask(() => {
        if (isComplexShiftSignup) {
          startComplexSignup();
        } else {
          signupMutation.mutate({
            eventId: shift.eventId,
            shift: shift,
            request: { shift: shift.shiftId, alias: user.userId },
          });
        }
      });
    }
  }, [
    forceSignup,
    isDelete,
    shift,
    isComplexShiftSignup,
    startComplexSignup,
    signupMutation,
    user.userId,
  ]);

  return (
    <>
      <Button
        fullWidth={!linkStyle}
        wrapText={linkStyle ? false : undefined}
        loading={signupMutation.isPending || deleteMutation.isPending}
        variant={linkStyle ? 'inline-link' : !isDelete ? 'primary' : undefined}
        // TODO: Fix this properly https://issues.amazon.com/issues/cce-4091
        data-testid={
          isComplexShiftSignup ? 'complexSignup.registerButton' : 'simpleSignup.registerButton'
        }
        data-aci-analytics-name={!isDelete ? signup_metric : `shifts_cancelDetails`}
        onClick={() => {
          if (isDelete) {
            if (!shift.complete && needsCancelConfirm) {
              setCancelConfirmVisible(true);
            } else {
              deleteMutation.mutate({
                eventId: shift.eventId,
                shift,
                request: { signupId: signup.signupId },
              });
            }
          } else {
            if (isComplexShiftSignup) {
              startComplexSignup();
            } else {
              signupMutation.mutate({
                eventId: shift.eventId,
                shift: shift,
                request: { shift: shift.shiftId, alias: user.userId },
              });
            }
          }
        }}
      >
        {!isDelete
          ? t('eventDetails.shiftDisplay.register')
          : !shift.complete
            ? t('eventDetails.shiftDisplay.cancel')
            : t('eventDetails.shiftDisplay.didNotAttend')}
      </Button>
      {/* Mount only when this shift can actually need the confirm: a Cloudscape Modal renders a
          [role="dialog"] root even while hidden, so an unconditional mount would add one per shift
          row on every event page. `|| cancelConfirmVisible` keeps it mounted through its own
          dismiss — confirming frees a spot, which flips needsCancelConfirm false mid-close. */}
      {(needsCancelConfirm || cancelConfirmVisible) && (
        <WaitlistCancelConfirmModal
          visible={cancelConfirmVisible}
          pending={deleteMutation.isPending}
          onDismiss={() => setCancelConfirmVisible(false)}
          onConfirm={() => {
            if (signup) {
              deleteMutation.mutate(
                {
                  eventId: shift.eventId,
                  shift,
                  request: { signupId: signup.signupId },
                },
                // Close on settle (success OR failure); the outcome surfaces via the page-level
                // useSignupAlerts, matching the LeaveWaitlistConfirmModal pattern.
                { onSettled: () => setCancelConfirmVisible(false) }
              );
            }
          }}
        />
      )}
    </>
  );
}

type WaitlistActionInput = {
  event: Event;
  shift: Shift;
  /** True when the user already holds an ACTIVE waitlist entry (render Leave); false renders Join. */
  isOnWaitlist: boolean;
  /** The user's ACTIVE waitlist entry for this shift; required to leave (supplies the waitlistEntryId). */
  waitlistEntry?: WaitlistEntry;
  /** One-click join deep link matched this row while it offers Join Waitlist (shift still full). */
  forceJoinWaitlist?: boolean;
  /** One-click leave deep link matched this row's ACTIVE entry: fire the leave without the confirm modal. */
  forceLeaveWaitlist?: boolean;
};

/**
 * Confirm dialog for leaving a waitlist (per the Figma): a warning Alert with Cancel / Leave waitlist.
 * Presentational — ToggleWaitlist owns the mutation and passes the handlers.
 */
function LeaveWaitlistConfirmModal({
  visible,
  pending,
  onDismiss,
  onConfirm,
}: {
  visible: boolean;
  pending: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const I18N = 'eventDetails.shiftDisplay.leaveWaitlistConfirm';
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={t(`${I18N}.title`)}
      closeAriaLabel={t(`${I18N}.cancel`)}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} data-testid="waitlist.cancelLeaveButton">
              {t(`${I18N}.cancel`)}
            </Button>
            <Button
              variant="primary"
              loading={pending}
              data-testid="waitlist.confirmLeaveButton"
              data-aci-analytics-name={VOLUNTEER_WAITLIST_LEAVE_CONFIRM}
              onClick={onConfirm}
            >
              {t('eventDetails.shiftDisplay.leaveWaitlist')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Alert type="warning">{t(`${I18N}.message`)}</Alert>
    </Modal>
  );
}

/**
 * Waitlist action for the VolunteerEventShiftsTable Action column — the waitlist sibling of
 * ToggleSignup, reused by the Event/Search/My Activity tables and the Waitlists tab.
 *
 * Join: a complex event opens the shared ComplexSignupDialog in waitlist mode (guests suppressed) to
 * collect t-shirt/questions/waiver; otherwise it joins directly.
 *
 * Leave: opens a confirm dialog (Figma) and, on confirm, calls useBulkLeaveWaitlist with a single-entry
 * batch built from waitlistEntry.waitlistEntryId. The button is a defensive no-op (disabled) when no
 * entry was supplied.
 */
export function ToggleWaitlist({
  event,
  shift,
  isOnWaitlist,
  waitlistEntry,
  forceJoinWaitlist,
  forceLeaveWaitlist,
}: WaitlistActionInput) {
  const { t } = useTranslation();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [leaveConfirmVisible, setLeaveConfirmVisible] = useState(false);
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const joinWaitlistMutation = useJoinWaitlist(event.eventId, shift.shiftId);
  const leaveWaitlistMutation = useBulkLeaveWaitlist(event.eventId, shift.shiftId);
  const hasForceFired = useRef(false);

  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const signupEvent = {
    ...event,
    questions: getSignupVisibleQuestions(event.questions, isEventCreationEnabled),
  };
  // Exclude guests from the complexity check — waitlist joins don't collect guests — so an event
  // whose only "complex" attribute is allowGuests joins directly without a dialog.
  const isComplexJoin = requiresComplexSignup(signupEvent, false);

  // One-click email deep links (join still-full / leave): fire the row's mutation once, skipping the
  // confirm modal — the email click is the explicit intent (mirrors ToggleSignup's forceCancelSignup).
  // Complex joins still need the dialog: the deep link cannot supply waiver/questions/t-shirt input.
  useEffect(() => {
    if (hasForceFired.current) return;

    if (forceJoinWaitlist && !isOnWaitlist) {
      hasForceFired.current = true;
      // GOLDEN PATH NOTE — defer past the current render cycle (see ToggleSignup).
      queueMicrotask(() => {
        if (isComplexJoin) {
          setDialogVisible(true);
        } else {
          joinWaitlistMutation.mutate({});
        }
      });
    } else if (forceLeaveWaitlist && isOnWaitlist && waitlistEntry) {
      hasForceFired.current = true;
      const { waitlistEntryId } = waitlistEntry;
      queueMicrotask(() => {
        leaveWaitlistMutation.mutate({ waitlistEntryId });
      });
    }
  }, [
    forceJoinWaitlist,
    forceLeaveWaitlist,
    isOnWaitlist,
    waitlistEntry,
    isComplexJoin,
    joinWaitlistMutation,
    leaveWaitlistMutation,
  ]);

  // Render row actions as link-styled text (variant="inline-link"), matching the Figma and
  // CoordinatorEventShiftsTable, which show row actions as text links rather than buttons. inline-link
  // keeps button semantics (onClick / loading / disabled / dialog) while rendering as inline text, so
  // the sticky Action column sizes to it like a link and never collapses. wrapText={false} keeps one line.
  if (isOnWaitlist) {
    return (
      <>
        <Button
          variant="inline-link"
          wrapText={false}
          loading={leaveWaitlistMutation.isPending}
          disabled={!waitlistEntry}
          data-testid="waitlist.leaveButton"
          data-aci-analytics-name={VOLUNTEER_WAITLIST_LEAVE_MODAL}
          onClick={() => setLeaveConfirmVisible(true)}
        >
          {t('eventDetails.shiftDisplay.leaveWaitlist')}
        </Button>
        <LeaveWaitlistConfirmModal
          visible={leaveConfirmVisible}
          pending={leaveWaitlistMutation.isPending}
          onDismiss={() => setLeaveConfirmVisible(false)}
          onConfirm={() => {
            if (waitlistEntry) {
              leaveWaitlistMutation.mutate(
                { waitlistEntryId: waitlistEntry.waitlistEntryId },
                // Close on settle (success OR failure); the outcome surfaces via the page-level
                // useWaitlistAlerts, matching how signup/cancel report results.
                { onSettled: () => setLeaveConfirmVisible(false) }
              );
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="inline-link"
        wrapText={false}
        loading={joinWaitlistMutation.isPending}
        data-testid="waitlist.joinButton"
        onClick={() => {
          if (isComplexJoin) {
            publishKatalMetric(VOLUNTEER_WAITLIST_JOIN_CLICK_COMPLEX_SIGNUP_MODAL, '');
            setDialogVisible(true);
          } else {
            publishKatalMetric(VOLUNTEER_WAITLIST_JOIN_CLICK_SIMPLE_SIGNUP, '');
            joinWaitlistMutation.mutate({});
          }
        }}
      >
        {t('eventDetails.shiftDisplay.joinWaitlist')}
      </Button>
      {isComplexJoin && (
        <ComplexSignupDialog
          mode="waitlist"
          event={event}
          shift={shift}
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
        />
      )}
    </>
  );
}
